/** How an enemy moves, by state (docs/spec/02 §4, §5): along a path, home to its slot, in its slot, away. */

import { attackFrom, becomeTrio } from './dive';
import { slotX, slotY } from './formation';
import { atan2, clamp, cos, DOWN, sin, turnToward, UP } from './math';
import type { PathPoint } from './path';
import { pathPoint } from './path';
import type { Difficulty } from './tuning';
import { BOMB, DIVE, ENTRY, FIELD } from './tuning';
import type { Enemy, GameState } from './types';

const P: PathPoint = { x: 0, y: 0, h: 0 };

/** Put an enemy where its path says, shifted as an escort or a second file is. */
function place(e: Enemy): void {
  if (!e.path) return;
  pathPoint(e.path, e.dist, P);
  e.heading = P.h;
  e.x = P.x + e.ox - sin(P.h) * e.side;
  e.y = P.y + e.oy + cos(P.h) * e.side;
}

/** A trio that loses a member to the bottom of the screen pays no bonus. */
function breakTrio(s: GameState, e: Enemy): void {
  if (e.trio !== 0 && !s.director.brokenTrios.includes(e.trio)) s.director.brokenTrios.push(e.trio);
}

function dropBombs(s: GameState, e: Enemy, d: Difficulty): void {
  if (e.bombs <= 0 || --e.bombTimer > 0) return;
  if (e.y > BOMB.lowestY || e.y < 24 || e.x < 8 || e.x > FIELD.w - 8 || s.player.status !== 'flying') {
    // Not now: look again next tick.
    e.bombTimer = 1;
    return;
  }
  // The angle off vertical toward the fighter, never steep.
  const a = clamp(atan2(s.player.x - e.x, FIELD.fighterY - e.y), -BOMB.maxAngle, BOMB.maxAngle);
  s.bombs.push({ x: e.x, y: e.y + 6, vx: sin(a) * d.bombSpeed, vy: cos(a) * d.bombSpeed });
  e.bombs--;
  e.bombTimer = BOMB.gap;
  s.events.push({ type: 'bomb', x: e.x, y: e.y });
}

function pathEnd(s: GameState, e: Enemy, d: Difficulty): void {
  e.path = null;
  e.ox = 0;
  e.oy = 0;
  e.side = 0;
  e.timer = 0;
  switch (e.after) {
    case 'home':
      e.state = e.slotted ? 'homing' : 'leaving';
      break;
    case 'beam':
      e.state = 'hover';
      break;
    case 'attack':
      // Like any dive, only at a fighter that is there (02 §5); otherwise it flies on and out.
      if (s.player.status === 'flying') attackFrom(s, e, d);
      else {
        e.after = 'leave';
        e.state = 'leaving';
      }
      break;
    default:
      e.state = 'leaving';
  }
}

function fly(s: GameState, e: Enemy, d: Difficulty): void {
  if (!e.path) {
    e.state = 'leaving';
    return;
  }
  e.dist += e.speed;
  place(e);
  // A trio's followers wait their turn at the start of the path.
  if (e.dist < 0) return;
  dropBombs(s, e, d);
  if (e.dist >= e.path.length) pathEnd(s, e, d);
}

function home(s: GameState, e: Enemy): void {
  const f = s.formation;
  const tx = slotX(f, e.col);
  const ty = slotY(f, e.row);
  const dx = tx - e.x;
  const dy = ty - e.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist <= e.speed + 0.5) {
    e.x = tx;
    e.y = ty;
    e.state = 'formed';
    e.group = 0;
    e.escorts = 0;
    e.timer = 0;
    e.bombs = 0;
    return;
  }
  const want = atan2(dy, dx);
  e.heading = dist < ENTRY.homeSnap ? want : turnToward(e.heading, want, ENTRY.homeTurn);
  e.x += cos(e.heading) * e.speed;
  e.y += sin(e.heading) * e.speed;
}

function sit(s: GameState, e: Enemy, d: Difficulty): void {
  e.x = slotX(s.formation, e.col);
  e.y = slotY(s.formation, e.row);
  e.heading = turnToward(e.heading, UP, 0.25);
  // The flash over, the trio sets off - but, like any dive, only at a fighter that is there (02 §5).
  if (e.state === 'morphing' && e.timer >= DIVE.morphFlash && s.player.status === 'flying' && !s.rescue) becomeTrio(s, e, d);
}

/** Off its path an enemy flies on, easing downward so that it always finds the bottom. */
function leave(s: GameState, e: Enemy): void {
  e.heading = turnToward(e.heading, DOWN, 0.03);
  e.x += cos(e.heading) * e.speed;
  e.y += sin(e.heading) * e.speed;
  const off = e.y > FIELD.h + 12 || e.y < -40 || e.x < -24 || e.x > FIELD.w + 24;
  if (!off) return;
  if (e.after === 'wrap' && e.slotted) {
    // Back in at the top, above its own place. A morph's leader comes home a Stinger again.
    if (e.kind === 'morph') {
      breakTrio(s, e);
      e.kind = 'stinger';
      e.variant = 0;
      e.trio = 0;
    }
    e.x = slotX(s.formation, e.col);
    e.y = -12;
    e.heading = DOWN;
    e.state = 'homing';
    e.timer = 0;
    return;
  }
  breakTrio(s, e);
  e.gone = true;
}

export function updateEnemies(s: GameState, d: Difficulty): void {
  // A trio adds to the list while it is being walked: only those here now move this tick.
  const count = s.enemies.length;
  for (let i = 0; i < count; i++) {
    const e = s.enemies[i];
    if (!e || e.gone) continue;
    e.timer++;
    switch (e.state) {
      case 'entering':
      case 'diving':
        fly(s, e, d);
        break;
      case 'homing':
        home(s, e);
        break;
      case 'formed':
      case 'morphing':
        sit(s, e, d);
        break;
      case 'hover':
        e.heading = turnToward(e.heading, DOWN, 0.2);
        break;
      case 'leaving':
        leave(s, e);
        break;
    }
  }
}
